import { useAuthContext } from "../../../../features/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { mapMarginAccount } from "../../mappers/marginAccountMapper";
import { MarginAccount } from "../../types/MarginAccount";
import { getValidatedMarginAccount } from "../../validators/marginAccountValidator";
import { getValidatedOptionOrder } from "../../validators/optionOrderValidator";
import { mapOptionOrder, mapOptionOrders } from "../../mappers/optionOrderMapper";
import { OptionOrder } from "../../types/OptionOrder";
import { HttpMethod } from "../../../shared/types/HttpMethod";
import { getValidatedOptionsOrder } from "../../validators/optionOrdersValidator";
import { useSnackbar } from "notistack";
import { createBinanceOptionsRequestService } from "./binanceOptionsRequestService";

export function useBinanceOptionsSecuredDataService() {
    const receiveWindow = 5000; // The number of milliseconds after timestamp the request is valid for

    const { enqueueSnackbar } = useSnackbar();
    const { userAuthData, isUserAuthenticated, binanceAuthData } = useAuthContext();
    const navigate = useNavigate();
    
    const requestService = createBinanceOptionsRequestService({
        userAuthData,
        isUserAuthenticated,
        binanceAuthData,
        navigate,
        enqueueSnackbar,
    });

    const sendOptionOrder = async (
        symbol: string, size: number, price: number, internalOrderId?: number | undefined
    ): Promise<OptionOrder | null> =>
        requestService.hasBrowserSecrets()
            ? signQueryAndSendOrder(symbol, size, price, internalOrderId)
            : sendOrder(symbol, size, price, internalOrderId);

    const signQueryAndSendOrder = async (
        symbol: string, size: number, price: number, internalOrderId?: number | undefined
    ): Promise<OptionOrder | null> => {
        const side = Math.sign(size) === 1 ? "BUY" : "SELL";
        const type = "LIMIT";

        const queryParamsForSigning = {
            symbol: symbol,
            side: side,
            quantity: size.toString(),
            price: price.toString(),
            type: type,
            recvWindow: receiveWindow.toString(),
        };

        const extraQueryParams = internalOrderId
            ? { internalOrderId: internalOrderId.toString() }
            : null;

        return requestService.signAndSendRequest(
            HttpMethod.Post, "api/binance-options-secured-data",
            queryParamsForSigning, extraQueryParams,
            getValidatedOptionOrder, mapOptionOrder);
    }

    const sendOrder = async (
        symbol: string, size: number, price: number, internalOrderId?: number | undefined
    ): Promise<OptionOrder | null> =>
        requestService.sendRequest(
            HttpMethod.Post, "api/binance-options-secured-data/server-signed",
            {
                symbol: symbol,
                quantity: size,
                price: price,
                internalOrderId: internalOrderId
            },
            getValidatedOptionOrder, mapOptionOrder);

    const cancelOptionOrder = async (
        symbol: string, orderId: number, internalOrderId?: number | null
    ): Promise<OptionOrder | null> =>
        requestService.hasBrowserSecrets()
            ? signQueryAndCancelOrder(symbol, orderId, internalOrderId)
            : cancelOrder(symbol, orderId, internalOrderId);

    const signQueryAndCancelOrder = async (
        symbol: string, orderId: number, internalOrderId?: number | null
    ): Promise<OptionOrder | null> => {
        const queryParamsForSigning = {
            symbol: symbol,
            orderId: orderId.toString(),
            recvWindow: receiveWindow.toString(),
        };

        const extraQueryParams = internalOrderId
            ? { internalOrderId: internalOrderId.toString() }
            : null;

        return requestService.signAndSendRequest(
            HttpMethod.Delete, "api/binance-options-secured-data", queryParamsForSigning, extraQueryParams,
            getValidatedOptionOrder, mapOptionOrder);
    }

    const cancelOrder = async (
        symbol: string, orderId: number, internalOrderId?: number | null
    ): Promise<OptionOrder | null> =>
        requestService.sendRequest(
            HttpMethod.Delete, "api/binance-options-secured-data/server-signed",
            {
                symbol,
                orderId,
                internalOrderId
            },
            getValidatedOptionOrder, mapOptionOrder);
    
    const getOptionsMarginAccount = (): Promise<MarginAccount | null> =>
        requestService.hasBrowserSecrets()
            ? signQueryAndGetMarginAccount()
            : getMarginAccount();

    const signQueryAndGetMarginAccount = (): Promise<MarginAccount | null> => {
        const queryParamsForSigning = {
            recvWindow: receiveWindow.toString(),
        };

        return requestService.signAndSendRequest(
            HttpMethod.Get, "api/binance-options-secured-data", queryParamsForSigning, null,
            getValidatedMarginAccount, mapMarginAccount);
    }

    const getMarginAccount = (): Promise<MarginAccount | null> =>
        requestService.sendRequest(
            HttpMethod.Get, "api/binance-options-secured-data/server-signed", null,
            getValidatedMarginAccount, mapMarginAccount);
    
    const getOpenOptionOrders = (): Promise<OptionOrder[] | null> =>
        requestService.hasBrowserSecrets()
            ? signQueryAndGetOpenOptionOrders()
            : getOpenOrders();

    const signQueryAndGetOpenOptionOrders = (): Promise<OptionOrder[] | null> => {
        const queryParamsForSigning = {
            recvWindow: receiveWindow.toString(),
        };

        return requestService.signAndSendRequest(
            HttpMethod.Get, "api/binance-options-secured-data/open-orders", queryParamsForSigning, null,
            getValidatedOptionsOrder, mapOptionOrders);
    }

    const getOpenOrders = (): Promise<OptionOrder[] | null> =>
        requestService.sendRequest(
            HttpMethod.Get, "api/binance-options-secured-data/open-orders/server-signed", null,
            getValidatedOptionsOrder, mapOptionOrders);

    return {
        sendOptionOrder,
        cancelOptionOrder,
        getOptionsMarginAccount,
        getOpenOptionOrders,
    };
}
